import { useCallback } from 'react';
import {
  ApiError,
  CbDataRequestInit,
  NonThrowingFetch,
  ReReadableOkResponse,
} from '@cbhq/data-layer/network';

import { wait } from '../../../utils/wait';

import { MockHandlerConfig } from './types';
import { useMockHandler } from './useMockHandler';

type NonThrowingFetchWithMocks = (
  url: string,
  init?: CbDataRequestInit,
) => ReturnType<NonThrowingFetch>;

type MockMiddleware = () => NonThrowingFetchWithMocks;

function logRequestInfo(
  url: string,
  init: CbDataRequestInit | undefined,
  response: ReReadableOkResponse,
) {
  /**
   * Mocked requests don't show up in the network tab, so we're logging them
   */
  if (process.env.NODE_ENV !== 'test') {
    console.groupCollapsed(url); // eslint-disable-line no-console
    console.log(`req, init:`, init); // eslint-disable-line no-console
    console.log(`res, `, response); // eslint-disable-line no-console
    console.groupEnd(); // eslint-disable-line no-console
  }
}

export function useMockMiddleware(
  handlers: MockHandlerConfig,
  pathPrefix?: string,
): MockMiddleware {
  const { getMockResponse } = useMockHandler(handlers, pathPrefix);

  const middleware = useCallback(() => {
    return async (url: string, init?: CbDataRequestInit) => {
      const response: unknown = getMockResponse(url, init);

      logRequestInfo(url, init, response as ReReadableOkResponse);

      if (response instanceof ApiError) {
        return response;
      }

      // Giving some loading time for us to see loaders while developing
      await wait(1000);

      return {
        requestTime: 0,
        timeUntilFirstByte: 0,
        timeUntilFirstChunk: 0,
        headers: init?.headers,
        status: 200,
        json: async () => response,
        text: async () => JSON.stringify(response),
        iterable: () => {
          throw new Error('Not implemented');
        },
        ok: true,
        body: init?.body,
        url,
      } as ReReadableOkResponse;
    };
  }, [getMockResponse]);

  return middleware;
}
